import React from 'react';
import { useAuth } from '../context/AuthContext';
import ThinHeader from '../components/ThinHeader';

const GuidePage = () => {
  const { user } = useAuth();

  return (
    <div className="guide-page">
      <ThinHeader
        homeTo="/"
        userLabel={user?.displayName || user?.username || user?.email || 'Player'}
        showAdminLink={user?.role === 'admin'}
      />

      <section className="guide-hero" id="top">
        <h1>How To Play and Host</h1>
        <p>Quick, simple, and built for friendly chaos.</p>
      </section>

      <section className="guide-section" id="how-to-play-sheet">
        <h2>New Player How To Play Sheet</h2>
        <p>Use this as a quick reference when you sit down for your first game.</p>

        <h3>Round Setup</h3>
        <ul>
          <li>Each player gets 3 face-down cards, then 3 face-up cards on top, then 3 hand cards.</li>
          <li>Goal: empty hand cards first, then face-up cards, then face-down cards.</li>
          <li>First player to clear everything wins the round.</li>
        </ul>

        <h3>Turn Flow</h3>
        <ul>
          <li>Play one or more valid cards from your hand (or current playable stack).</li>
          <li>If you cannot play, you must pick up the pile (unless a special rule overrides it).</li>
          <li>When your hand is gone, you continue with your face-up cards.</li>
          <li>After face-up cards are gone, you finish with your face-down cards.</li>
        </ul>

        <h3>Special Cards</h3>
        <ul>
          <li>2: Wild restart. Can play on almost anything and resets the pile value.</li>
          <li>3: Wild pressure card. Next player must pick up unless they can play a 3 forward.</li>
          <li>7: Restriction card. Next player must play 7 or lower.</li>
          <li>8: Transparent card. Takes the value of the card underneath it.</li>
          <li>10: Bowel (burn). Clears the current play pile.</li>
          <li>J: Reverses direction of play.</li>
        </ul>

        <h3>Normal Rank Order</h3>
        <p>4, 5, 6, 7, 8, 9, 10, J, Q, K, A (with 2 and 3 as special wild cards).</p>

        <h3>Beginner Tips</h3>
        <ul>
          <li>Save 2 or 10 for emergency resets when the pile gets too high.</li>
          <li>Use 7 to trap strong hands and slow the next player down.</li>
          <li>If you must leave mid-round, leave game and a bot can finish the round for you.</li>
        </ul>
      </section>

      <section className="guide-section" id="get-started">
        <h2>Get Started</h2>
        <p>Join a room, play a round, and come back later when you want. It is built for quick drop-in play.</p>
        <ul>
          <li>Use Join for rooms in lobby status.</li>
          <li>Use Watch / Queue when a game is in progress or seats are full.</li>
          <li>Return to Waiting Room if you are already seated in that game.</li>
          <li>You can leave after a round and rejoin later from the Lounge list.</li>
          <li>Light trash talk is encouraged. Keep it fun.</li>
        </ul>
      </section>

      <section className="guide-section" id="join-leave-tables">
        <h2>Join and Leave Tables</h2>
        <ul>
          <li>Join Game seats you at the table right away when the room is in lobby.</li>
          <li>Reserve Spot adds you to the next-round queue if a game is already active.</li>
          <li>Leave Table lets you step out. You can come back later and join again.</li>
          <li>Need to leave in a hurry during an active round? Leave Game and a bot will take over your seat for the rest of that round.</li>
          <li>If everyone leaves or goes inactive at the same time, the room can be collapsed/reset by host or moderators.</li>
        </ul>
      </section>

      <section className="guide-section" id="create-room">
        <h2>Create and Name Rooms</h2>
        <p>Clear room names help players find your game and tournament tables.</p>
        <ul>
          <li>Create public rooms for drop-in players.</li>
          <li>Create private rooms for invite-only matches.</li>
          <li>Use NPC Dealer if your table needs support.</li>
          <li>Increase NPC difficulty when your group wants a harder game.</li>
        </ul>
      </section>

      <section className="guide-section" id="pwa">
        <h2>P / W / A Room Stats</h2>
        <p>These labels show room activity at a glance.</p>
        <ul>
          <li>P: Players seated in the game.</li>
          <li>W: Waitlist players waiting for a seat.</li>
          <li>A: Active connected users currently in the room.</li>
        </ul>
      </section>

      <section className="guide-section" id="live-games">
        <h2>Live Tournament vs Long-Play Tournament</h2>

        <h3>Live Tournament</h3>
        <p>Everyone plays together in real time. Designed for nights when your crew is all online at once.</p>
        <ul>
          <li>Players sit down, cards are dealt, and the game runs like a normal session.</li>
          <li>An optional inactivity timer (10, 15, 20, 30, or 60 minutes) can be enabled on the room.</li>
          <li>When the timer is on and the table goes idle, seated players who have not acted are automatically moved back to the waiting room so active players can continue and score.</li>
          <li>This prevents one absent player from holding up the whole tournament leaderboard.</li>
          <li>If you are moved out, rejoin from the Lounge and queue for the next round.</li>
        </ul>

        <h3>Long-Play Tournament</h3>
        <p>Designed for games that unfold over days, weeks, or months — like correspondence chess by post.</p>
        <ul>
          <li>Players check in when they can and post their moves on their own schedule.</li>
          <li>No inactivity timer applies. The table stays open between turns as long as needed.</li>
          <li>Great for groups spread across time zones or with unpredictable schedules.</li>
          <li>Points still count toward the weekly or monthly leaderboard as rounds complete.</li>
        </ul>

        <h3>General Tournament Notes</h3>
        <ul>
          <li>Enable Tournament Room on create, then pick weekly or monthly recurrence for the leaderboard period.</li>
          <li>Name your tournament clearly so players know which room to join.</li>
          <li>Admins and moderators can switch mode or adjust the timer from Room Settings at any time.</li>
          <li>Points appear on the period leaderboard in Lobby and Stats as rounds complete.</li>
        </ul>
      </section>

      <section className="guide-section" id="bots">
        <h2>NPC and Start Game Basics</h2>
        <ul>
          <li>Use Add NPC To Empty Tile to fill open seats in lobby.</li>
          <li>You need at least one human seated before starting a game.</li>
          <li>Use Deal Cards &amp; Start Swap Phase to begin once the table is ready.</li>
          <li>If the table setup goes wrong, use Remove All NPCs and set it again.</li>
        </ul>
      </section>

      <section className="guide-section" id="inactive-users">
        <h2>Inactive Players and Boots</h2>
        <ul>
          <li>In Live Tournament rooms with the inactivity timer on, idle seated players are automatically moved to the waiting room when time expires.</li>
          <li>This keeps the game moving and the leaderboard fair — no one can block progress by going silent.</li>
          <li>Long-Play Tournament rooms never auto-boot. All players stay seated between moves.</li>
          <li>Host and moderators can also manually collapse inactive users at any time using Collapse Non-Active Users.</li>
          <li>If you are removed from a stale round, rejoin from the Lounge and queue for the next round.</li>
        </ul>
      </section>
    </div>
  );
};

export default GuidePage;
